import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import * as React from "react";

import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-200 disabled:text-gray-400 disabled:border-gray-200 enabled:cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90  rounded-full",
        outline: "border-2 border-border bg-background hover:bg-accent ounded-full",
        ghost: "hover:bg-accent hover:text-accent-foreground rounded-md ",
        brand: "bg-brand text-brand-foreground hover:bg-brand/90 rounded-full",
        "brand-outline":
          "border-2 border-brand bg-background text-brand hover:bg-brand-muted rounded-full",
        primary:
          "bg-[#7666f5] text-white hover:bg-[#6758e8] focus-visible:ring-[#7B6CF6] rounded-full",
        "outline-purple":
          "border-2 border-[#7B6CF6] bg-white text-[#7B6CF6] hover:bg-[#f6f4ff] focus-visible:ring-[#7B6CF6] rounded-full",
        "outline-gray":
          "border-2 border-[#cccccc] bg-white hover:bg-[#f5f5f5] focus-visible:ring-[#7B6CF6] rounded-full",
        secondary:
          "bg-[#D4CCFF] text-[#5F51D9] hover:bg-[#C9BFFF] focus-visible:ring-[#5F51D9] rounded-full",
        solid:
          "bg-[#5F51D9] text-white hover:bg-[#4a3ec4] focus-visible:ring-[#5F51D9] rounded-full",
      },
      size: {
        default: "h-10 px-4 py-2 text-sm",
        sm: "h-[40px] px-4 text-sm",
        md: "h-[48px] px-6 text-base",
        lg: "h-[56px] px-6 text-base",
        xl: "h-[64px] px-8 text-lg",
        auto: "px-4 py-2 text-[10px]",
      },
      fullWidth: {
        true: "w-full",
        false: "w-auto",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      fullWidth,
      asChild = false,
      isLoading,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    // boolean 타입 보장을 위한 명시적 변환
    const isFullWidth = fullWidth === true;
    const isDisabled = !!(disabled || isLoading);

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, fullWidth: isFullWidth, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {isLoading ? (
          <span className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {children}
          </span>
        ) : (
          children
        )}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
